import { CommonModule, DatePipe, NgForOf, NgIf } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { AuthService } from "../../../core/services/auth.service";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { RouterLink } from "@angular/router";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDropdown,
  TuiLink,
  TuiLoader,
  tuiLoaderOptionsProvider,
  TuiSizeL,
  TuiSizeS,
  TuiTextfield,
} from "@taiga-ui/core";
import {
  TuiBreadcrumbs,
  TuiDataListDropdownManager,
  TuiDataListWrapper,
  TuiFade,
  TuiItemsWithMore,
  TuiPagination,
  TuiStatus,
  TuiStringifyContentPipe,
  TuiSwitch,
  TuiTabs,
} from "@taiga-ui/kit";
import { TuiCell, TuiNavigation, TuiSearch } from "@taiga-ui/layout";
import { TuiTable } from "@taiga-ui/addon-table";
import {
  TuiComboBoxModule,
  TuiInputSliderModule,
  TuiTextfieldControllerModule,
} from "@taiga-ui/legacy";
import { ForgotResetPasswordService } from "../../../core/services/forgot-reset-password.service.service";
import { INurse } from "../../../model/interface/nurse";
import { ClientService } from "../../../core/services/client.service";
import { loadListState, saveListState } from "../../../shared/utils/list-state";
import { formatClientIdentifier } from "../../../shared/utils/client-identifier";
import {
  canUnarchiveClient,
  TYPE_CHANGE_UNARCHIVE_MESSAGE,
} from "../../../shared/utils/client-archive";
import {
  RecordRenewalConfirmPayload,
  RecordRenewalDialogComponent,
} from "../../../shared/components/record-renewal-dialog/record-renewal-dialog.component";
import { DisableUnlessPermissionDirective } from "../../../core/directives/disable-unless-permission.directive";

@Component({
  selector: 'app-expired-clients',
  imports: [
    FormsModule,
    NgForOf,
    NgIf,
    TuiAppearance,
    TuiDataListWrapper,
    TuiBreadcrumbs,
    TuiButton,
    TuiDataList,
    TuiPagination,
    TuiDropdown,
    TuiFade,
    TuiTable,
    TuiLink,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiCell,
    TuiStatus,
    TuiItemsWithMore,
    TuiSearch,
    ReactiveFormsModule,
    TuiSwitch,
    CommonModule,
    TuiInputSliderModule,
    TuiTextfieldControllerModule,
    RouterLink,
    TuiComboBoxModule,
    TuiStringifyContentPipe,
    TuiLoader,
    TuiDataListDropdownManager,
    RecordRenewalDialogComponent,
    DisableUnlessPermissionDirective,
  ],
  templateUrl: './expired-clients.component.html',
  styleUrl: './expired-clients.component.scss',
  providers: [DatePipe, tuiLoaderOptionsProvider({ size: "xl" })],
})
export class ExpiredClientsComponent implements OnInit {
  protected readonly breadcrumbs = ["Royal Hospital", "Expiring Soon (7 days)"];
  protected readonly sizes = ["m"] as const;
  protected open = false;
  protected openDropdownsize: TuiSizeL | TuiSizeS = "s";
  protected openDropdown = false;
  protected size = this.sizes[0];
  isLoading = false;
  clientData: any;
  paginatedData: INurse[] = [];
  clientService = inject(ClientService);
  showResults: string = "";
  filters = {
    searchText: "",
    status: null,
    archived: false,
    registrationType: "One Year", // Filter for One Year registration type
  };
  pageIndex = 1;
  pageSize = 10;
  total: number = 0;
  sortField = "expiryDate";
  sortOrder: number = 1; // Ascending order to show most recently expired first
  openMap: { [nurseId: string]: boolean } = {};
  loggedInUserCanEdit = false;
  renewDialogOpen = false;
  renewBusy = false;
  renewClientTarget: any = null;
  private authService = inject(AuthService);

  constructor(
    private datePipe: DatePipe,
    private resetPasswordService: ForgotResetPasswordService,
    private alerts: TuiAlertService
  ) {}

  // Form setup
  protected readonly form = new FormGroup({
    search: new FormControl(""),
    select: new FormControl(""),
  });

  protected value = null;

  protected readonly items = [
    { status: "Active", value: true },
    { status: "Inactive", value: false },
  ];

  protected stringify(item: { status: string; value: boolean } | null): string {
    if (!item) return "";
    return item.status;
  }

  listRowClientId(client: any): string {
    return formatClientIdentifier(client);
  }

  private readonly listStateKey = "expired-clients-list";

  ngOnInit(): void {
    this.restoreListState();
    this.loadExpiredClients();
    this.checkUserPermission();
  }

  private restoreListState(): void {
    const restored = loadListState(this.listStateKey, {
      filters: this.filters,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
    });
    this.filters = { ...this.filters, ...restored.filters };
    this.pageIndex = restored.pageIndex;
    this.pageSize = restored.pageSize;
    this.sortField = restored.sortField;
    this.sortOrder = restored.sortOrder;
  }

  private rememberListState(): void {
    saveListState(this.listStateKey, {
      filters: this.filters,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
    });
  }

  loadExpiredClients(): void {
    this.rememberListState();
    const payload = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      filters: {
        searchText: this.filters.searchText,
        status: this.filters.status ? (this.filters.status as { value: boolean }).value : null,
        archived: this.filters.archived,
      },
      sortField: this.sortField,
      sortOrder: this.sortOrder,
    };

    this.isLoading = true;

    this.clientService.getPagedExpiredClients(payload).subscribe({
      next: (res: any) => {
        if (res && res.length > 0 && res[0].data) {
          this.clientData = res[0].data;
          this.total = res[0].metadata[0]?.total || 0;
        } else {
          this.clientData = [];
          this.total = 0;
        }
      },
      error: (error) => {
        console.error("Error fetching expired client data:", error);
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  getRegistrationTypeBgColor(registrationType: string): string {
    switch (registrationType) {
      case 'Life Time':
        return 'purple';
      case 'One Year':
        return 'blue';
      default:
        return 'gray';
    }
  }

  toggleDropdown(clientId: string): void {
    this.openMap[clientId] = !this.openMap[clientId];
  }

  sort(): void {
    this.sortOrder = this.sortOrder === 1 ? -1 : 1;
    this.loadExpiredClients();
  }

  applyFilters(newFilters: any): void {
    this.filters = { ...this.filters, ...newFilters };
    this.loadExpiredClients();
  }

  applySort(): void {
    this.loadExpiredClients();
  }

  onEnter(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      this.applyFilters({ searchText: this.filters.searchText });
    }
  }

  onPageChange(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.loadExpiredClients();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = Number(pageSize);
    this.pageIndex = 1;
    this.loadExpiredClients();
  }

  clearSearch(): void {
    this.filters.searchText = "";
    this.filters.status = null;
    this.applyFilters({ searchText: this.filters.searchText, status: null });
  }

  getPageCount(): number {
    return Math.ceil(this.total / this.pageSize);
  }

  resetUserPassword(email: any): void {
    const payload = {
      email: email,
    };
    this.resetPasswordService.requestResetPasswordClient(payload).subscribe({
      next: (response) => {
        this.alerts
          .open("Password reset information sent to client's email", {
            label: "Success",
            appearance: "success",
          })
          .subscribe();
      },
      error: (error) => {
        console.error("Error resetting password:", error);
      },
    });
  }

  toggleActive(clientId: string): void {
    this.clientService.getOneClientById(clientId).subscribe((client: any) => {
      if (!client) return;
      
      if (!client.active) {
        if (client.expiryDate) {
          const currentDate = new Date();
          const expiryDate = new Date(client.expiryDate);
          
          if (expiryDate < currentDate) {
            this.alerts
              .open("Cannot reactivate client. The expiry date has passed. Use Renew to extend membership and reactivate.", {
                label: "Warning",
                appearance: "warning",
              })
              .subscribe();
            return;
          }
        }
      }
      
      const updatedClient = { ...client, active: !client.active };

      this.clientService.updateClient(updatedClient).subscribe({
        next: (response) => {
          this.clientData = response;
          const status = updatedClient.active ? "activated" : "deactivated";
          this.alerts
            .open(`Client has been ${status} successfully.`, {
              label: "Success",
              appearance: "success",
            })
            .subscribe();
          this.loadExpiredClients();
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.error("Error updating client data:", error);
          this.alerts
            .open("Failed to update client data.", {
              label: "Error",
              appearance: "error",
            })
            .subscribe();
        },
      });
    });
  }

  readonly canUnarchiveClient = canUnarchiveClient;

  openRenewDialog(user: any): void {
    this.renewClientTarget = user;
    this.renewDialogOpen = true;
  }

  onRenewConfirm(payload: RecordRenewalConfirmPayload): void {
    const id = this.renewClientTarget?._id;
    if (!id || this.renewBusy) return;
    this.renewBusy = true;
    this.clientService.renewClient(id, payload).subscribe({
      next: (res) => {
        this.renewBusy = false;
        this.renewDialogOpen = false;
        this.renewClientTarget = null;
        if (res?.success) {
          this.alerts
            .open(res.message || "Renewal recorded successfully.", {
              label: "Success",
              appearance: "success",
            })
            .subscribe();
          this.loadExpiredClients();
        }
      },
      error: (err) => {
        this.renewBusy = false;
        const message =
          err?.error?.message || err?.message || "Failed to record renewal.";
        this.alerts
          .open(message, { label: "Error", appearance: "error" })
          .subscribe();
      },
    });
  }

  toggleArchive(clientId: string): void {
    this.clientService.getOneClientById(clientId).subscribe((client: any) => {
      if (!client) return;
      if (client.archived && !canUnarchiveClient(client)) {
        this.alerts
          .open(TYPE_CHANGE_UNARCHIVE_MESSAGE, {
            label: "Cannot unarchive",
            appearance: "warning",
          })
          .subscribe();
        return;
      }
      const updatedClient = { ...client, archived: !client.archived };

      this.clientService.updateClient(updatedClient).subscribe({
        next: (response) => {
          const status = updatedClient.archived ? "archived" : "unarchived";
          this.alerts
            .open(`Client has been ${status} successfully.`, {
              label: "Success",
              appearance: "success",
            })
            .subscribe();
          this.loadExpiredClients();
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.error("Error updating client data:", error);
          this.alerts
            .open(
              error?.error?.message ||
                error?.message ||
                "Failed to update client data.",
              {
                label: "Error",
                appearance: "error",
              },
            )
            .subscribe();
        },
      });
    });
  }
  checkUserPermission(): void {
    this.loggedInUserCanEdit = this.authService.hasPermission("CLIENT_UPDATE");
  }

  getDaysRemaining(expiryDate: string): number {
    const currentDate = new Date();
    const expiry = new Date(expiryDate);
    const timeDiff = expiry.getTime() - currentDate.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return daysDiff;
  }

  getExpiryStatus(expiryDate: string): 'expired' | 'expiring-soon' {
    const daysRemaining = this.getDaysRemaining(expiryDate);
    return daysRemaining < 0 ? 'expired' : 'expiring-soon';
  }
}
